import {
  AskStrikeUpButton,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  InfoBox,
  RefreshButton,
  Stack,
  Table,
  Text,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import {
  MdAdd,
  MdDelete,
  MdEdit,
  MdRefresh,
  MdToggleOff,
  MdToggleOn,
} from "react-icons/md";
import { authService } from "services/authService";
import { companyService, CompanyUser } from "services/companyService";
import { getThemeColor } from "theme/colors";
import InviteUserModal from "./components/InviteUserModal";

const UsersManagement = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);

  const currentUser = authService.getCurrentUser();
  const companyId = currentUser?.companyId || "";
  const isAdmin = authService.isAdmin();

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        const data = await companyService.getCompanyUsers();
        setUsers(data);
        setError("");
      } catch (err: any) {
        setError(err.response?.data?.message || t("app.users.loadFailed"));
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [t]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await companyService.getCompanyUsers();
      setUsers(data);
      setError("");
    } catch (err: any) {
      setError(err.response?.data?.message || t("app.users.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleInviteSuccess = () => {
    setShowInviteModal(false);
    loadUsers();
    setSuccess(t("app.users.inviteSuccess"));
    setTimeout(() => setSuccess(""), 3000);
  };

  const handleToggleRole = async (userId: string, currentRole: string) => {
    const newRole = currentRole === "Admin" ? "User" : "Admin";

    if (!window.confirm(t("app.users.changeRoleConfirm", { role: newRole }))) {
      return;
    }

    try {
      await companyService.updateUserRole(userId, newRole);
      await loadUsers();
      setSuccess(t("app.users.roleUpdateSuccess"));
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || t("app.users.updateRoleFailed"));
    }
  };

  const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
    const action = currentStatus
      ? t("app.users.disable")
      : t("app.users.enable");

    if (!window.confirm(t("app.users.toggleStatusConfirm", { action }))) {
      return;
    }

    try {
      await companyService.toggleUserStatus(userId, !currentStatus);
      await loadUsers();
      setSuccess(
        t("app.users.statusUpdateSuccess", {
          action: currentStatus
            ? t("app.users.disabled")
            : t("app.users.enabled"),
        }),
      );
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          t("app.users.toggleStatusFailed", { action }),
      );
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (!window.confirm(t("app.users.deleteConfirm", { name: userName }))) {
      return;
    }

    try {
      await companyService.deleteUser(userId);
      await loadUsers();
      setSuccess(t("app.users.deleteSuccess"));
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || t("app.users.deleteFailed"));
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await companyService.resendInvitation(invitationId);
      setSuccess(t("app.users.resendSuccess"));
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || t("app.users.resendFailed"));
    }
  };

  const handleCancelInvitation = async (
    invitationId: string,
    email: string,
  ) => {
    if (!window.confirm(t("app.users.cancelInvitationConfirm", { email }))) {
      return;
    }

    try {
      await companyService.cancelInvitation(invitationId);
      await loadUsers();
      setSuccess(t("app.users.cancelInvitationSuccess"));
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.message || t("app.users.cancelInvitationFailed"),
      );
    }
  };

  if (!isAdmin) {
    return (
      <Card.Root
        bg={getThemeColor("card.bg")}
        borderColor={getThemeColor("card.border")}
      >
        <Card.Body>
          <Text textAlign="center" color="red.500">
            {t("app.users.accessDenied")}
          </Text>
        </Card.Body>
      </Card.Root>
    );
  }

  if (loading) {
    return (
      <Stack gap={4}>
        <Card.Root>
          <Card.Body>
            <Text textAlign="center">{t("app.users.loading")}</Text>
          </Card.Body>
        </Card.Root>
      </Stack>
    );
  }

  return (
    <Stack gap={4}>
      <Flex justify="space-between" align="center">
        <Heading>{t("app.users.title")}</Heading>
        <HStack gap={3}>
          <AskStrikeUpButton context={t("app.askStrikeUp.context.users")} />
          <RefreshButton onClick={loadUsers} loading={loading} />
          <Button onClick={() => setShowInviteModal(true)} intent="primary">
            <MdAdd />
            {t("app.users.inviteUser")}
          </Button>
        </HStack>
      </Flex>

      {error && (
        <InfoBox variant="error">
          <HStack justify="space-between">
            <Text color="red.700">{error}</Text>
            <Button onClick={() => setError("")} size="sm" intent="ghost">
              {t("app.users.dismiss")}
            </Button>
          </HStack>
        </InfoBox>
      )}

      {success && (
        <InfoBox variant="success">
          <Text color="green.700">{success}</Text>
        </InfoBox>
      )}

      <Card.Root
        bg={getThemeColor("card.bg")}
        borderWidth="1px"
        borderColor={getThemeColor("card.border")}
        borderRadius="md"
        overflow="hidden"
      >
        <Card.Body p={0}>
          {users.length === 0 ? (
            <Flex p={8} justify="center">
              <Text>{t("app.users.noUsers")}</Text>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table.Root minW="600px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>
                      {t("app.users.name")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.users.email")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.users.role")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.users.status")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.users.created")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.users.actions")}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {users.map((user) => (
                    <Table.Row key={user.id}>
                      <Table.Cell>
                        <HStack>
                          <Avatar.Root name={user.email} size="sm" />
                          <Text fontWeight="medium">{user.email}</Text>
                        </HStack>
                      </Table.Cell>
                      <Table.Cell>{user.email}</Table.Cell>
                      <Table.Cell>
                        <Badge
                          colorPalette={
                            user.role === "Admin" ? "purple" : "gray"
                          }
                        >
                          {user.role}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge
                          colorPalette={
                            user.status === "Pending"
                              ? "yellow"
                              : user.status === "Active"
                                ? "green"
                                : "red"
                          }
                        >
                          {user.status === "Pending"
                            ? t("app.users.pending")
                            : user.status === "Active"
                              ? t("app.users.active")
                              : t("app.users.disabled")}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        {new Date(user.createdAt).toLocaleDateString()}
                      </Table.Cell>
                      <Table.Cell>
                        {user.status === "Pending" ? (
                          <HStack>
                            <Button
                              onClick={() =>
                                handleResendInvitation(user.invitationId!)
                              }
                              size="sm"
                              intent="ghost"
                              title={t("app.users.resendInvitation")}
                            >
                              <MdRefresh />
                            </Button>
                            <Button
                              onClick={() =>
                                handleCancelInvitation(
                                  user.invitationId!,
                                  user.email,
                                )
                              }
                              size="sm"
                              variant="ghost"
                              colorPalette="red"
                              title={t("app.users.cancelInvitation")}
                            >
                              <MdDelete />
                            </Button>
                          </HStack>
                        ) : (
                          <HStack>
                            <Button
                              onClick={() =>
                                handleToggleRole(user.id, user.role)
                              }
                              size="sm"
                              intent="ghost"
                              disabled={user.id === currentUser?.id}
                              title={
                                user.id === currentUser?.id
                                  ? t("app.users.cannotChangeOwnRole")
                                  : t("app.users.changeRole")
                              }
                            >
                              <MdEdit />
                            </Button>
                            <Button
                              onClick={() =>
                                handleToggleStatus(user.id, user.isActive)
                              }
                              size="sm"
                              variant="ghost"
                              colorPalette="orange"
                              disabled={user.id === currentUser?.id}
                              title={
                                user.id === currentUser?.id
                                  ? t("app.users.cannotChangeOwnStatus")
                                  : user.isActive
                                    ? t("app.users.disableUser")
                                    : t("app.users.enableUser")
                              }
                            >
                              {user.isActive ? <MdToggleOn /> : <MdToggleOff />}
                            </Button>
                            <Button
                              onClick={() =>
                                handleDeleteUser(user.id, user.email)
                              }
                              size="sm"
                              variant="ghost"
                              colorPalette="red"
                              disabled={user.id === currentUser?.id}
                              title={
                                user.id === currentUser?.id
                                  ? t("app.users.cannotDeleteOwnAccount")
                                  : t("app.users.deleteUser")
                              }
                            >
                              <MdDelete />
                            </Button>
                          </HStack>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Card.Body>
      </Card.Root>

      {showInviteModal && (
        <InviteUserModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          onSuccess={handleInviteSuccess}
        />
      )}
    </Stack>
  );
};

export default UsersManagement;
